// 新規キャンバスの初回の名前入力ダイアログ。
//
// 表示するかどうかはサーバの表示指示（bootstrap の meta.initialNaming）だけで決める。クライアントの
// 「オブジェクトが 0 件」や初回のマウントでは判定しない（既存の空キャンバスや、取得失敗時の空表示を
// 新規作成と取り違えないため）。ボタンは 1 つだけで、入力が空（空白だけを含む）なら「あとで決める」、
// 名前があれば「決定」になる。閉じるボタン・キャンセルは置かず、Esc や外側のクリックでも閉じない。
// 表示中は背景のキャンバスの入力とショートカットを止める（ネイティブの modal dialog が背景を
// 操作不能にし、キーボード・クリップボードのイベントはダイアログの外へ伝えない）。

import { Button, Form, Modal, OutlinedInputText, useFormSubmitting } from "@meiden-connect/ui";
import { CANVAS_TITLE_MAX_LENGTH } from "@refinement-canvas/domain";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  type SyntheticEvent,
} from "react";
import {
  CanvasApiHttpError,
  useCanvasApi,
  type InitialCanvasNameRequest,
  type InitialCanvasNameResponse,
} from "../../../lib/canvas-api";
import { DEFAULT_CANVAS_TITLE, normalizeCanvasTitle } from "../../../lib/canvas-title";
import { useShowToast } from "../notice/use-show-toast";
import { useCanvasDocStore, type CanvasMetaState } from "../store/canvas-doc-store";

/** 確定の要求の待ち時間の上限。超えたら打ち切り、確定済みかどうかをスナップショットで確かめる。 */
export const INITIAL_CANVAS_NAME_REQUEST_TIMEOUT_MS = 15_000;

const DIALOG_TITLE = "キャンバス名を決めてください";
const SAVE_FAILED_MESSAGE =
  "キャンバス名を保存できませんでした。通信状態を確認して、もう一度お試しください。";
const CLOSED_BY_OTHER_MESSAGE =
  "ほかの画面または参加者の操作が先に反映されたため、名前の入力を閉じました。キャンバス名は左上から変更できます。";
const NOT_SAVED_MESSAGE =
  "ほかの画面または参加者の操作が先に反映されたため、入力した名前は保存していません。キャンバス名は左上から変更できます。";
const CLOSED_BY_TRASH_MESSAGE = "キャンバスがゴミ箱へ移動されたため、名前の入力を閉じました。";
const FORBIDDEN_MESSAGE = "キャンバスを作成した利用者だけが、初回の名前を決められます。";
const NOT_FOUND_MESSAGE = "キャンバスが見つからないため、名前を保存できませんでした。";

/**
 * 表示中に、ダイアログの外を対象にしたものを背景のキャンバスへ伝えないイベント。
 * keyup は止めない（キーボードでのパンなど、押下中の状態を keyup で解除する処理を取り残さないため。
 * keyup を合図に編集操作を行うショートカットは無い）。
 */
const SHIELDED_EVENT_TYPES = ["keydown", "copy", "cut", "paste"] as const;

/** 送信の失敗後に、入力欄が有効へ戻るのを待つ上限（描画の回数。約 0.5 秒）。 */
const REFOCUS_MAX_FRAMES = 30;

interface Props {
  readonly canvasId: string;
}

/** 1 回の送信の文脈。 */
interface Submission {
  readonly generation: string;
  readonly request: InitialCanvasNameRequest;
  /** 送信前のサーバ確定の名前（楽観更新の巻き戻し先）。 */
  readonly previousTitle: string;
  /**
   * 送信の直後（楽観更新の後）のサーバ確定のメタ（参照）。doc store はメタを更新するたびに新しい参照へ
   * 置き換えるため、応答までに配信・スナップショットでメタが更新されたかを参照の一致で確かめる。
   */
  readonly submittedMeta: CanvasMetaState;
}

/** 失敗の扱い。 */
type FailureOutcome =
  /** 確定済みかもしれない失敗。取り直したスナップショットで最新状態を確かめた。 */
  | {
      readonly kind: "readback";
      readonly title: string;
      readonly pendingGeneration?: string | undefined;
    }
  /** 確定できない状態（ゴミ箱・作成者以外・キャンバスなし）。説明して閉じる。 */
  | { readonly kind: "closed"; readonly message: string }
  /** 確定していない失敗。入力を残して再試行させる。 */
  | { readonly kind: "retry" };

export function InitialCanvasNameDialog({ canvasId }: Props): JSX.Element | null {
  const canvasApi = useCanvasApi();
  const showToast = useShowToast();
  const updateLocalMeta = useCanvasDocStore((s) => s.updateLocalMeta);
  const requestMetaRefresh = useCanvasDocStore((s) => s.requestMetaRefresh);
  const prompt = useCanvasDocStore((s) => s.meta.initialNaming);
  const isTrashed = useCanvasDocStore((s) => s.meta.isTrashed ?? false);
  // 名前入力を表示している初回状態の識別値。表示指示が届いたときに開き、確定・外部の解決で閉じる。
  const [activeGeneration, setActiveGeneration] = useState<string | undefined>(undefined);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  // 他の参加者の命名・図形の配置は、サーバ確定状態（committed）で見る。自分の楽観更新（送信中の名前）を
  // 他者の命名と取り違えないよう、送信中は判定しない。
  const committedTitle = useCanvasDocStore((s) => s.committedMeta.title);
  const hasCommittedObjects = useCanvasDocStore((s) =>
    activeGeneration === undefined ? false : hasAnyKey(s.committedObjects)
  );
  // 解決済みにした初回状態。古いスナップショットの表示指示が後から届いても、同じ初回状態では開き直さない。
  const resolvedGenerationsRef = useRef<Set<string>>(new Set());
  const composingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  // 送信の応答を待つ間にキャンバスを離れた場合、次に開いたキャンバスの doc store へ書き込まない。
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // 開いた直後は入力欄へフォーカスを置く。あわせて、ダイアログの外を対象にしたキーボード・クリップボードの
  // イベント（ダイアログ内の文字などをクリックしてフォーカスが body へ外れた場合など）が、背景のキャンバスの
  // ショートカット（window / document で受けるもの）へ届かないよう、window の捕捉段階で止める。
  // ダイアログの中を対象にしたイベントは、Modal の onKeyDown などで外へ伝えない。
  useEffect(() => {
    if (activeGeneration === undefined) return undefined;
    inputRef.current?.focus();
    const shield = (event: Event): void => {
      const dialog = dialogRef.current;
      if (dialog !== null && event.target instanceof Node && dialog.contains(event.target)) return;
      event.stopPropagation();
    };
    for (const type of SHIELDED_EVENT_TYPES) window.addEventListener(type, shield, true);
    return () => {
      for (const type of SHIELDED_EVENT_TYPES) window.removeEventListener(type, shield, true);
    };
  }, [activeGeneration]);

  // サーバの表示指示が届いたら開く（解決済みにした初回状態は除く）。
  useEffect(() => {
    if (prompt === undefined || activeGeneration !== undefined) return;
    if (resolvedGenerationsRef.current.has(prompt.generation)) return;
    setActiveGeneration(prompt.generation);
  }, [activeGeneration, prompt]);

  // 開いている間に、別の画面での確定（配信）・他の参加者の命名・図形の配置・ゴミ箱への移動が起きたら、
  // 入力を送れない状態になったことを説明して閉じる（サーバも同じ条件で初回状態を解決済みとして扱う）。
  useEffect(() => {
    if (activeGeneration === undefined || submitting) return;
    let notice: { readonly level: "info" | "warning"; readonly message: string } | undefined;
    if (isTrashed) {
      notice = { level: "warning", message: CLOSED_BY_TRASH_MESSAGE };
    } else if (
      prompt?.generation !== activeGeneration ||
      hasCommittedObjects ||
      normalizeCanvasTitle(committedTitle) !== DEFAULT_CANVAS_TITLE
    ) {
      notice = { level: "info", message: CLOSED_BY_OTHER_MESSAGE };
    }
    if (notice === undefined) return;
    resolvedGenerationsRef.current.add(activeGeneration);
    setActiveGeneration(undefined);
    setErrorMessage(undefined);
    showToast(notice.level, notice.message);
  }, [
    activeGeneration,
    committedTitle,
    hasCommittedObjects,
    isTrashed,
    prompt,
    showToast,
    submitting,
  ]);

  if (activeGeneration === undefined) return null;
  const generation = activeGeneration;
  const trimmedDraft = draft.trim();

  /** この画面がまだ同じキャンバスを表示しているか（離れた後の応答で doc store を書き換えない）。 */
  const isCurrentCanvas = (): boolean =>
    mountedRef.current && useCanvasDocStore.getState().canvasId === canvasId;

  /**
   * サーバの名前（確定の応答、または確かめるために取り直したスナップショットの名前）を背景のキャンバス名へ
   * 反映する。送信の後にメタが一度も更新されていない（楽観更新のまま、または送信前のまま）ときだけ、応答の
   * 名前で置き換える。送信の後に配信・スナップショットでメタが更新されていれば、画面の名前が楽観更新した名前や
   * 送信前の名前と同じでも、それが応答より後に届いた改名なのか、古いスナップショットや配信の遅れによるものかを
   * 応答だけでは区別できないため、応答の名前と異なるときは書かずに、権威スナップショットでの取り直しを要求する
   * （古い応答で、後から届いた改名を巻き戻さない）。それ以外の名前は後から届いた改名として残す。
   */
  const applyServerTitle = (submission: Submission, title: string): void => {
    const currentMeta = useCanvasDocStore.getState().committedMeta;
    const current = currentMeta.title;
    if (title === current) return;
    if (currentMeta === submission.submittedMeta) {
      updateLocalMeta({ title });
      return;
    }
    const optimistic =
      submission.request.decision === "name" ? submission.request.title : undefined;
    if (current === optimistic || current === submission.previousTitle) {
      requestMetaRefresh();
    }
  };

  /**
   * 保存されなかった（確定していない）送信の楽観更新を取り消す。画面の名前が楽観更新した名前のままなら、
   * すぐに送信前の名前へ戻す（サムネイルの保存などメタの名前以外の更新があっても、未保存の名前を残さない。
   * 再試行の送信前の名前にも未保存の名前を使わない）。送信の後にメタが更新されていれば、同じ名前への後から
   * 届いた改名と区別できないため、あわせて権威スナップショットでの取り直しを要求する。画面の名前が別の名前に
   * なっていれば、後から届いた改名として残す。
   */
  const revertOptimisticTitle = (submission: Submission): void => {
    if (submission.request.decision !== "name") return;
    const currentMeta = useCanvasDocStore.getState().committedMeta;
    if (currentMeta.title !== submission.request.title) return;
    updateLocalMeta({ title: submission.previousTitle });
    if (currentMeta !== submission.submittedMeta) requestMetaRefresh();
  };

  /** 確定した（または解決済みと分かった）状態へ合わせて閉じる。 */
  const close = (submission: Submission, latestTitle: string): void => {
    resolvedGenerationsRef.current.add(submission.generation);
    setActiveGeneration(undefined);
    setErrorMessage(undefined);
    applyServerTitle(submission, latestTitle);
    updateLocalMeta({ initialNaming: undefined });
  };

  /**
   * 確定の結果（またはスナップショットで確かめた最新状態）へ合わせる。同じ初回状態がまだ有効なら、
   * 確定できていないため入力を残して再試行できるようにする。
   */
  const settle = (
    submission: Submission,
    result: {
      readonly applied: boolean;
      readonly title: string;
      readonly pendingGeneration?: string | undefined;
    },
    /** 確定の応答（response）か、通信の打ち切り後に取り直したスナップショット（readback）か。 */
    origin: "response" | "readback"
  ): void => {
    if (!result.applied && result.pendingGeneration === submission.generation) {
      revertOptimisticTitle(submission);
      setErrorMessage(SAVE_FAILED_MESSAGE);
      return;
    }
    close(submission, result.title);
    if (result.applied) return;
    if (submission.request.decision === "name") {
      // 先に別の画面・参加者が決めていて、入力した名前を保存していない場合だけ知らせる
      // （通信の打ち切り後の確認で、自分の名前が保存済みと分かった場合は知らせない）。
      if (result.title !== submission.request.title) showToast("info", NOT_SAVED_MESSAGE);
      return;
    }
    // 「あとで決める」でも、確定の応答で先行した操作（配信で届いていなかった REST・MCP 経由の改名・
    // 配置や、別の画面の確定）が分かったときは、閉じた理由を知らせる。通信の打ち切り後の確認では、
    // 自分の確定と区別できないため知らせない。
    if (origin === "response") showToast("info", CLOSED_BY_OTHER_MESSAGE);
  };

  const requestWithTimeout = (
    request: InitialCanvasNameRequest
  ): Promise<InitialCanvasNameResponse> =>
    withRequestTimeout((signal) =>
      canvasApi.resolveInitialCanvasName(canvasId, request, { signal })
    );

  /**
   * 失敗を分類する。4xx は確定していない失敗として扱い、通信の打ち切り・通信断・5xx は確定済みかも
   * しれないため、スナップショットを取り直して初回状態がまだ有効かを確かめる。取り直しも同じ上限時間で
   * 打ち切り、確かめられなければ確定していない失敗として扱う（送信中のまま閉じられなくならないように）。
   */
  const classifyFailure = async (error: unknown): Promise<FailureOutcome> => {
    if (error instanceof CanvasApiHttpError && error.status >= 400 && error.status < 500) {
      if (error.status === 409) return { kind: "closed", message: CLOSED_BY_TRASH_MESSAGE };
      if (error.status === 403) return { kind: "closed", message: FORBIDDEN_MESSAGE };
      if (error.status === 404) return { kind: "closed", message: NOT_FOUND_MESSAGE };
      return { kind: "retry" };
    }
    try {
      const snapshot = await withRequestTimeout((signal) =>
        canvasApi.fetchCanvasBootstrap(canvasId, undefined, { resync: true, signal })
      );
      // キャンバスが（削除などで）存在しない場合は、確定できないため説明して閉じる。
      if (snapshot.notCreated === true) return { kind: "closed", message: NOT_FOUND_MESSAGE };
      return {
        kind: "readback",
        title: snapshot.meta.title,
        ...(snapshot.meta.initialNaming !== undefined
          ? { pendingGeneration: snapshot.meta.initialNaming.generation }
          : {}),
      };
    } catch {
      return { kind: "retry" };
    }
  };

  const handleSubmit = async (): Promise<void> => {
    // 日本語入力の変換中は確定しない（変換確定の Enter で送信しない）。
    if (composingRef.current) return;
    const request: InitialCanvasNameRequest =
      trimmedDraft.length > 0
        ? { decision: "name", generation, title: trimmedDraft }
        : { decision: "later", generation };
    const previousTitle = useCanvasDocStore.getState().committedMeta.title;
    setSubmitting(true);
    setErrorMessage(undefined);
    // 背景のキャンバス名は先に新しい名前へ替える（楽観更新）。失敗したら元へ戻す。
    if (request.decision === "name") {
      updateLocalMeta({ title: request.title });
    }
    const submission: Submission = {
      generation,
      request,
      previousTitle,
      submittedMeta: useCanvasDocStore.getState().committedMeta,
    };
    try {
      const response = await requestWithTimeout(submission.request);
      if (!isCurrentCanvas()) return;
      settle(
        submission,
        {
          applied: response.applied,
          title: response.meta.title,
          pendingGeneration: response.initialNaming?.generation,
        },
        "response"
      );
    } catch (error) {
      const outcome = await classifyFailure(error);
      if (!isCurrentCanvas()) return;
      if (outcome.kind === "readback") {
        settle(
          submission,
          {
            applied: false,
            title: outcome.title,
            pendingGeneration: outcome.pendingGeneration,
          },
          "readback"
        );
      } else if (outcome.kind === "closed") {
        revertOptimisticTitle(submission);
        resolvedGenerationsRef.current.add(submission.generation);
        setActiveGeneration(undefined);
        updateLocalMeta({ initialNaming: undefined });
        showToast("warning", outcome.message);
      } else {
        revertOptimisticTitle(submission);
        setErrorMessage(SAVE_FAILED_MESSAGE);
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <Modal
      ref={dialogRef}
      open={true}
      aria-label={DIALOG_TITLE}
      className="canvas-initial-name-dialog"
      // 表示中は背景のキャンバスのショートカット（window / document で受けるもの）へキーボード・
      // クリップボードの操作を伝えない。ネイティブの既定動作（文字入力・Enter での送信）は止めない。
      onKeyDown={stopPropagation}
      onCopy={stopPropagation}
      onCut={stopPropagation}
      onPaste={stopPropagation}
    >
      <Form onSubmit={handleSubmit} className="canvas-initial-name-dialog__form">
        <h2 className="canvas-initial-name-dialog__title">{DIALOG_TITLE}</h2>
        <OutlinedInputText
          label="キャンバス名"
          inputProps={{
            ref: inputRef,
            value: draft,
            onChange: setDraft,
            placeholder: DEFAULT_CANVAS_TITLE,
            // 上限はサーバのタイトル制約と同じ値・同じ数え方にそろえる。
            maxLength: CANVAS_TITLE_MAX_LENGTH,
            autoFocus: true,
            autoComplete: "off",
            onKeyDown: (event: ReactKeyboardEvent<HTMLInputElement>) => {
              if (event.key !== "Enter") return;
              const nativeEvent = event.nativeEvent as globalThis.KeyboardEvent & {
                readonly isComposing?: boolean | undefined;
                readonly keyCode?: number | undefined;
              };
              // 変換確定の Enter は IME の既定動作に委ね、確定しない。Safari は変換確定の後に
              // keyCode 229 の Enter を送るため、その Enter による送信だけを止める。
              if (nativeEvent.isComposing) return;
              if (nativeEvent.keyCode === 229) event.preventDefault();
            },
            onCompositionStart: () => {
              composingRef.current = true;
            },
            onCompositionEnd: () => {
              composingRef.current = false;
            },
          }}
        />
        {errorMessage !== undefined ? (
          <p role="alert" className="canvas-initial-name-dialog__error">
            {errorMessage}
          </p>
        ) : null}
        <div className="canvas-initial-name-dialog__actions">
          {/* ボタンは 1 つだけ。名前が空（空白だけを含む）なら既定名のまま解決する「あとで決める」、
              名前があれば保存する「決定」。どちらも押した直後に処理が行われるため action を使う。 */}
          <Button type="submit" variant={trimmedDraft.length > 0 ? "action emphasis" : "action"}>
            {trimmedDraft.length > 0 ? "決定" : "あとで決める"}
          </Button>
        </div>
        <RefocusAfterFailedSubmit inputRef={inputRef} failed={errorMessage !== undefined} />
      </Form>
    </Modal>
  );
}

/**
 * 送信が失敗して入力欄が使えるようになったら、入力欄へフォーカスを戻す（再試行しやすくする）。
 * Form は送信中にフォーム全体（fieldset）を無効にし、送信の完了を知らせた後で有効へ戻すため、
 * 有効になるまで描画のたびに確かめてからフォーカスする（無効の間の focus は効かない）。
 */
function RefocusAfterFailedSubmit({
  inputRef,
  failed,
}: {
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly failed: boolean;
}): null {
  const formSubmitting = useFormSubmitting();
  useEffect(() => {
    if (formSubmitting || !failed) return undefined;
    let frame = 0;
    let attempts = 0;
    const tryFocus = (): void => {
      const input = inputRef.current;
      if (input === null) return;
      if (input.matches(":disabled")) {
        attempts += 1;
        if (attempts < REFOCUS_MAX_FRAMES) frame = window.requestAnimationFrame(tryFocus);
        return;
      }
      input.focus();
    };
    tryFocus();
    return () => window.cancelAnimationFrame(frame);
  }, [failed, formSubmitting, inputRef]);
  return null;
}

/**
 * 要求を上限時間（INITIAL_CANVAS_NAME_REQUEST_TIMEOUT_MS）で打ち切る。確定の要求と、確定済みかを
 * 確かめるスナップショットの取り直しで共有する。signal の中止だけでは、通信の前の待ち（アクセス
 * トークンの取得・更新など、signal を受け取らない処理）が続く間は要求が終わらないため、上限時間に
 * なったら結果を待つこと自体もやめて失敗にする（中止済みの signal により、その後の通信も始まらない）。
 */
async function withRequestTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let timer: number | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = window.setTimeout(() => {
      controller.abort();
      reject(new DOMException("要求が上限時間内に終わりませんでした。", "TimeoutError"));
    }, INITIAL_CANVAS_NAME_REQUEST_TIMEOUT_MS);
  });
  try {
    return await Promise.race([run(controller.signal), timeout]);
  } finally {
    window.clearTimeout(timer);
  }
}

function stopPropagation(event: SyntheticEvent): void {
  event.stopPropagation();
}

/** オブジェクトを 1 件でも持つか（件数に比例する走査を避け、最初のキーで判定する）。 */
function hasAnyKey(record: Readonly<Record<string, unknown>>): boolean {
  for (const key in record) {
    if (Object.prototype.hasOwnProperty.call(record, key)) return true;
  }
  return false;
}
